import type { SVGIconProps } from "@faire/slate-icons/types";
import { FilterToggle } from "@faire/slate/components/filter";
import { tsv } from "@faire/slate/lib/tsv";
import React from "react";

import { SlateStyleOverride } from "../../../../components/shared";
import type { RecommendedFilter } from "../constants";
import { RECOMMENDED_FILTERS } from "../constants";

import { APPLIED_CHIP_OUTLINE } from "./appliedChipOutline";
import { InsiderKeyIcon, TopShopShieldIcon } from "./ChipIcons";

interface RecommendedFiltersSectionProps {
  appliedToggles: Set<string>;
  onToggle: (key: string) => void;
  /** Draws the separating rule. False when this is first in the body. */
  hasContentAbove: boolean;
}

/**
 * One-tap filters at the top of the modal, above the sections. Several are the
 * same filters the chip row offers and share their keys, so toggling here moves
 * the chip too.
 */
export const RecommendedFiltersSection: React.FC<
  RecommendedFiltersSectionProps
> = ({ appliedToggles, onToggle, hasContentAbove }) => (
  <div className={sectionStyle.cls({ hasContentAbove })}>
    <span className="type-fs-paragraph-medium text-fs-text-primary">
      Recommended
    </span>
    <SlateStyleOverride className={APPLIED_CHIP_OUTLINE}>
      <div className="flex flex-wrap gap-(--slate-dimensions-2)">
        {RECOMMENDED_FILTERS.map((filter) => (
          <FilterToggle
            key={filter.key}
            Icon={iconFor(filter)}
            applied={appliedToggles.has(filter.key)}
            label={filter.label}
            onClick={() => onToggle(filter.key)}
          />
        ))}
      </div>
    </SlateStyleOverride>
  </div>
);

const iconFor = (
  filter: RecommendedFilter
): React.FC<SVGIconProps> | undefined => {
  if (filter.icon === "insider") {
    return InsiderKeyIcon;
  }
  if (filter.icon === "topShop") {
    return TopShopShieldIcon;
  }
  return undefined;
};

const sectionStyle = tsv({
  base: "flex flex-col gap-(--slate-dimensions-3)",
  variants: {
    hasContentAbove: {
      true: "border-fs-border-muted border-t pt-(--slate-dimensions-4)",
      // Flush with the top of the body when nothing sits above it; the
      // modal's own header already provides the separation.
      false: null,
    },
  },
});
